import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  IconButton,
  Pagination,
} from '@mui/material';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Footer from 'pages/Footer';
import { getPatientDataDetailsList } from 'reduxStore/patientDataDetails/patientDataPointListByPatientID/patientDataPointListByPatientIDSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import DocumentViewer from './DocumentViewer';
import { toggleAppBarLoading } from 'reduxStore/appLoader/toggleAppBarLoadingSlice';

// 1. Define the TypeScript interface for your Row data
interface PatientRow {
  id: number; // DataGrid requires a unique 'id' field by default
  patientName: string;
  mrn: string;
  uuid: string;
  patientAadhaar: string;
  parentAadhaar: string;
  parentPan: string;
  parentPassport: string;
  parentIncomeCert: string;
  ayushmanCard: string;
  remarks: string;
  status: 'Mark as Verified' | 'Mark as Incomplete' | 'Mark as Failed';
}

// 2. Define the Columns
const columns: GridColDef[] = [
  { field: 'patientName', headerName: 'Patient Name', width: 180, flex: 1 },
  { field: 'mrn', headerName: 'MRN', width: 120 },
  { field: 'uuid', headerName: 'UUID', width: 150 },
  { field: 'patientAadhaar', headerName: 'Patient Aadhaar', width: 160 },
  { field: 'parentAadhaar', headerName: 'Parent Aadhaar', width: 160 },
  { field: 'parentPan', headerName: 'Parent PAN', width: 130 },
  { field: 'parentPassport', headerName: 'Parent Passport', width: 150 },
  { field: 'parentIncomeCert', headerName: 'Income Cert', width: 150 },
  { field: 'ayushmanCard', headerName: 'Ayushman Card', width: 160 },
  {
    field: 'remarks',
    headerName: 'Remarks',
    width: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        {params.value || 'N/A'}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    renderCell: (params: GridRenderCellParams) => {
      const status = params.value as string;
      let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' =
        'default';

      if (status === 'Mark as Verified') color = 'success';
      if (status === 'Mark as Incomplete') color = 'warning';
      if (status === 'Mark as Failed') color = 'error';

      return <Chip label={status} color={color} size='small' variant='outlined' />;
    },
  },
];

// 3. Mock Data
const rows: PatientRow[] = [
  {
    id: 1,
    patientName: 'Atanu Pandit',
    mrn: '10001YY',
    uuid: '3bda2df2-b650-42dc-91cf-e50d3534384e',
    patientAadhaar: '4532-1122-9088',
    parentAadhaar: '2231-9988-1122',
    parentPan: 'BRPSA1122L',
    parentPassport: 'A1234567',
    parentIncomeCert: 'IC-2023-001',
    ayushmanCard: 'AYU-99011',
    remarks: 'Documents matched',
    status: 'Mark as Verified',
  },
  {
    id: 2,
    patientName: 'Dipankar Bera',
    mrn: '100022R',
    uuid: '8940582d-4908-4fd7-8f85-91b2d1a1bcc9',
    patientAadhaar: '9088-2231-5544',
    parentAadhaar: '5544-2231-9088',
    parentPan: 'CPKPA9988M',
    parentPassport: 'B9988776',
    parentIncomeCert: 'IC-2023-002',
    ayushmanCard: 'AYU-88122',
    remarks: 'Income cert missing',
    status: 'Mark as Incomplete',
  },
  {
    id: 3,
    patientName: 'Ruchi Roy',
    mrn: '10001V5',
    uuid: '40424e31-833e-43a1-8650-cd4e19b948bf',
    patientAadhaar: '1122-3344-5566',
    parentAadhaar: '6655-4433-2211',
    parentPan: 'DLKPV4433K',
    parentPassport: 'C1122334',
    parentIncomeCert: 'IC-2023-003',
    ayushmanCard: 'AYU-77233',
    remarks: 'Aadhaar rejected',
    status: 'Mark as Failed',
  },
  {
    id: 4,
    patientName: 'Anjali Gupta',
    mrn: '10004V5',
    uuid: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
    patientAadhaar: '7788-9900-1122',
    parentAadhaar: '1122-0099-8877',
    parentPan: 'EPLPG1100J',
    parentPassport: 'D4455667',
    parentIncomeCert: 'IC-2023-004',
    ayushmanCard: 'AYU-66344',
    remarks: 'N/A',
    status: 'Mark as Verified',
  },
  {
    id: 5,
    patientName: 'Vikram Singh',
    mrn: '10005V5',
    uuid: '710b962e-041c-11e1-9234-0123456789ab',
    patientAadhaar: '5566-7788-9900',
    parentAadhaar: '9900-8877-6655',
    parentPan: 'FLKPS9900I',
    parentPassport: 'E7788990',
    parentIncomeCert: 'IC-2023-005',
    ayushmanCard: 'AYU-55455',
    remarks: 'PAN card unclear',
    status: 'Mark as Incomplete',
  },
  {
    id: 6,
    patientName: 'Sanya Iyer',
    mrn: '10006V5',
    uuid: '982c3d4e-1234-5678-abcd-ef1234567890',
    patientAadhaar: '3344-5566-7788',
    parentAadhaar: '7788-6655-4433',
    parentPan: 'GLKPI7788H',
    parentPassport: 'F0011223',
    parentIncomeCert: 'IC-2023-006',
    ayushmanCard: 'AYU-44566',
    remarks: 'Urgent',
    status: 'Mark as Verified',
  },
  {
    id: 7,
    patientName: 'Amit Reddy',
    mrn: '10007V5',
    uuid: 'bcdef123-4567-890a-bcde-f1234567890a',
    patientAadhaar: '1100-2211-3322',
    parentAadhaar: '3322-1122-0011',
    parentPan: 'HLKPR1122G',
    parentPassport: 'G3344556',
    parentIncomeCert: 'IC-2023-007',
    ayushmanCard: 'AYU-33677',
    remarks: 'Passport invalid',
    status: 'Mark as Failed',
  },
  {
    id: 8,
    patientName: 'Meera Das',
    mrn: '10008V5',
    uuid: '550e8400-e29b-11d4-a716-446655440011',
    patientAadhaar: '9911-8822-7733',
    parentAadhaar: '7733-8822-9911',
    parentPan: 'ILKPD8822F',
    parentPassport: 'H6677889',
    parentIncomeCert: 'IC-2023-008',
    ayushmanCard: 'AYU-22788',
    remarks: 'Photos pending',
    status: 'Mark as Incomplete',
  },
  {
    id: 9,
    patientName: 'Rohan Mehta',
    mrn: '10009V5',
    uuid: '660f9511-f30c-22e5-b827-557766551122',
    patientAadhaar: '8822-7733-6644',
    parentAadhaar: '6644-7733-8822',
    parentPan: 'JLKPM7733E',
    parentPassport: 'I9900112',
    parentIncomeCert: 'IC-2023-009',
    ayushmanCard: 'AYU-11899',
    remarks: 'Verified by Admin',
    status: 'Mark as Verified',
  },
  {
    id: 10,
    patientName: 'Sneha Rao',
    mrn: '10010V5',
    uuid: '771g0622-041d-33f6-c938-668877662233',
    patientAadhaar: '7733-6644-5555',
    parentAadhaar: '5555-6644-7733',
    parentPan: 'KLKPR6644D',
    parentPassport: 'J2233445',
    parentIncomeCert: 'IC-2023-010',
    ayushmanCard: 'AYU-00911',
    remarks: 'System error',
    status: 'Mark as Failed',
  },
  {
    id: 11,
    patientName: 'Kabir Khan',
    mrn: '10011V5',
    uuid: '882h1733-152e-44a7-d049-779988773344',
    patientAadhaar: '6644-5555-4466',
    parentAadhaar: '4466-5555-6644',
    parentPan: 'LLKPK5555C',
    parentPassport: 'K5566778',
    parentIncomeCert: 'IC-2023-011',
    ayushmanCard: 'AYU-11022',
    remarks: 'Pending signature',
    status: 'Mark as Incomplete',
  },
  {
    id: 12,
    patientName: 'Ishani Bose',
    mrn: '10012V5',
    uuid: '993i2844-263f-55b8-e15a-880099884455',
    patientAadhaar: '5555-4466-3377',
    parentAadhaar: '3377-4466-5555',
    parentPan: 'MLKPB4466B',
    parentPassport: 'L8899001',
    parentIncomeCert: 'IC-2023-012',
    ayushmanCard: 'AYU-22133',
    remarks: 'Clear',
    status: 'Mark as Verified',
  },
  {
    id: 13,
    patientName: 'Aditya Malviya',
    mrn: '10013V5',
    uuid: '004j3955-3740-66c9-f26b-991100995566',
    patientAadhaar: '4466-3377-2288',
    parentAadhaar: '2288-3377-4466',
    parentPan: 'NLKPM3377A',
    parentPassport: 'M1122334',
    parentIncomeCert: 'IC-2023-013',
    ayushmanCard: 'AYU-33244',
    remarks: 'Incomplete Address',
    status: 'Mark as Incomplete',
  },
  {
    id: 14,
    patientName: 'Zoya Qureshi',
    mrn: '10014V5',
    uuid: '115k4066-4851-77da-037c-002211006677',
    patientAadhaar: '3377-2288-1199',
    parentAadhaar: '1199-2288-3377',
    parentPan: 'OLKPQ2288Z',
    parentPassport: 'N4455667',
    parentIncomeCert: 'IC-2023-014',
    ayushmanCard: 'AYU-44355',
    remarks: 'Invalid documents',
    status: 'Mark as Failed',
  },
  {
    id: 15,
    patientName: 'Yashwardhan Jha',
    mrn: '10015V5',
    uuid: '226l5177-5962-88eb-148d-113322117788',
    patientAadhaar: '2288-1199-0000',
    parentAadhaar: '0000-1199-2288',
    parentPan: 'PLKPJ1199Y',
    parentPassport: 'P7788990',
    parentIncomeCert: 'IC-2023-015',
    ayushmanCard: 'AYU-55466',
    remarks: 'Check mark',
    status: 'Mark as Verified',
  },
];

export default function DocumentVerificationList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(
      getPatientDataDetailsList({
        patientid: '23', // Pass your hardcoded ID here
        pageNo: 1,
      }),
    );
  }, []);

  const newColumns: GridColDef[] = [
    {
      field: 'image',
      headerName: 'View',
      align: 'left',
      minWidth: 100,
      renderCell: (params: GridCellParams) => (
        <Button
          startIcon={<VisibilityIcon />}
          variant='outlined'
          sx={{ textTransform: 'none' }}
          onClick={() => {
            console.log('row data===', params.row);
            setDocViwerActive(true);
            setSelectedItem(Object.assign(params.row, { patientName: 'Selected Patient' }));
            // history.push(`/patientdetails/${params.row.patientId}?documentid=${params.row.id}`);
          }}
        >
          View
        </Button>
      ),
    },
    {
      field: 'id',
      headerName: 'Patient details I\'d',
      type: 'string',
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      align: 'left',
      minWidth: 150,
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'string',
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      align: 'left',
      minWidth: 500,
    },
    {
      field: 'detailsType',
      headerName: 'Patient Details Type',
      type: 'string',
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      align: 'left',
      minWidth: 250,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      type: 'string',
      editable: false,
      sortable: true,
      align: 'left',
      disableColumnMenu: true,
      flex: 1,
    },
  ];
  const patientPointDataList = useAppSelector((state: any) => state.getPatientDataDetailsRes);

  const [pageListCount, setPageListCount] = useState('');
  const [_listCount, setListCount] = useState('');
  const [dataGridArray, setDataArray] = React.useState([] as any[]);
  console.log('dataGridArray', dataGridArray);

  const [docViwerActive, setDocViwerActive] = React.useState(false);
  console.log('docViwerActive', docViwerActive);
  const dialogClose = (childState: any) => {
    setDocViwerActive(childState);
  };
  const [selectedItem, setSelectedItem] = React.useState<any>(({} as any) || undefined);
  const [page, setPage] = React.useState(1);
  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    dispatch(getPatientDataDetailsList({ patientid: '23', pageNo: value }));
  };

  useEffect(() => {
    dispatch(getPatientDataDetailsList({ patientid: '23', pageNo: page }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(toggleAppBarLoading(patientPointDataList?.loading))
    patientPointDataList?.pageCount !== undefined
      ? setPageListCount(String(patientPointDataList?.pageCount))
      : setPageListCount('0');

    patientPointDataList?.getPatientDataDetailsRes?.count !== undefined
      ? setListCount(String(patientPointDataList?.getPatientDataDetailsRes?.count))
      : setListCount('0');

    if (patientPointDataList?.getPatientDataDetailsRes?.patientDetailsList !== undefined) {
      const dataPointArr = patientPointDataList?.getPatientDataDetailsRes?.patientDetailsList.map(
        (element: any) => ({
          id: element.id,
          patientId: element.patientId,
          description: element.description,
          detailsType: element.sectionType,
          startDate: dayjs(element.startDate).format('dddd, MMMM D, YYYY h:mm A'),
          date: element.startDate,
          image:
            element?.patientDocument?.document !== undefined
              ? element?.patientDocument?.document
              : '',
          imageId:
            element?.patientDocument?.id !== undefined
              ? String(element?.patientDocument?.id)
              : undefined,
        }),
      );
      setDataArray(dataPointArr);
    } else {
      setDataArray([] as any[]);
      setPageListCount(String(patientPointDataList?.pageCount));
    }
  }, [patientPointDataList]);
  return (
    <>
      <Card>
        <CardHeader
          title={
            <Grid container direction='row' justifyContent='space-between' alignItems='center'>
              <Grid item>
                <Grid container direction='row' justifyContent='flex-start' alignItems='center'>
                  <Grid item>
                    <IconButton onClick={() => navigate('/patient-document-verification')}>
                      <ArrowBackIcon />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography variant='h5' sx={{ color: '#009E49' }}>
                      Patient Document Verification List:
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  variant='contained'
                  color='primary'
                  sx={{ float: 'right', textTransform: 'none' }}
                  onClick={() => navigate('/patient-document-verification')}
                >
                  Go Document Verification Page
                </Button>
              </Grid>
            </Grid>
          }
        />
        <CardContent>
          {/* <Box sx={{ height: 'calc(100vh - 480px)', overflow: 'auto' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
              },
            }}
          />
        </Box> */}
          <Grid
            container
            direction='row'
            justifyContent='flex-end'
            alignItems='flex-start'
            spacing={2}
            pt={2}
          >
            <Typography sx={{ paddingTop: '5px' }}>Page: {page}</Typography>
            <Pagination
              color='primary'
              count={Number(pageListCount)}
              page={page}
              onChange={handleChange}
            />
          </Grid>
          <Box sx={{ height: 'calc(100vh - 260px)', overflow: 'auto' }}>
            <DataGrid
              rows={dataGridArray}
              columns={newColumns}
              hideFooter
              hideFooterPagination
              autoHeight
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              sx={{
                '& .MuiDataGrid-columnHeader[data-field="id"]': {
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'white',
                  zIndex: 3,
                },
              }}
            />
          </Box>
          
        </CardContent>
      </Card>
      <Footer />
      {docViwerActive ? (
        <DocumentViewer
          dialogState={docViwerActive}
          dialogClose={dialogClose}
          value={selectedItem}
          page={page}
        />
      ) : null}
    </>
  );
}
